import { css } from '@emotion/react';
import { TextFieldProps } from '@mui/material';
import {
  DatePicker,
  DatePickerProps,
  DateView,
  PickerShortcutChangeImportance,
  PickersShortcutsItem,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { endOfMonth, isValid, startOfMonth } from 'date-fns';
import { debounce } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { defaultCurrency } from '../../services/state/AppConfigStateJ';
import { CurrencyCode } from '../../util/currency-util';
import { ErrorText } from '../Form/FormComponents';
import { defaultShortcuts, isDateInputValid, shortcutsSx } from './Calendar';

export type ICalendarCurrencyProps = Pick<
  DatePickerProps<Date>,
  'minDate' | 'maxDate' | 'onClose' | 'onOpen' | 'closeOnSelect' | 'disabled' | 'sx'
> & {
  initialValue?: Date;
  /** Controlled date. While it is passed the caller owns what is shown, edits included. */
  value?: Date | null;
  /** Every move of the shown date, including one the user has not finished making or cannot be valid. */
  onChange: (newDate: Date | null) => void;
  /** The date the picker treats as decided: a committed selection, or the popup being dismissed. */
  onAccept?: (newDate: Date | null) => void;
  shortcutItems?: PickersShortcutsItem<Date | null>[];
  /** 'set' leaves the popup open on a shortcut click, so several can be clicked in a row. */
  shortcutChangeImportance?: PickerShortcutChangeImportance;
  textFieldProps?: Partial<TextFieldProps>;
  debounceTime?: number;
  selectedCurrency?: CurrencyCode;
};

const fieldWithErrorCss = css`
  position: relative;
`;

// Out of flow, so the message neither grows the compact bar the picker sits in nor moves the field.
const errorBelowFieldCss = css`
  position: absolute;
  top: 100%;
  left: 0;
  white-space: nowrap;
`;

// Function to convert date based on currency change
function convertDateForCurrencyChange(date: Date | null, isUSD: boolean): Date {
  if (!date) {
    return new Date();
  }

  if (isUSD) {
    // Changing from CCY to USD: if date is month-year only, use last day of month
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);

    // If date is the first day of the month, it's likely month-year only
    if (date.getTime() === monthStart.getTime()) {
      return monthEnd;
    }
    return date;
  } else {
    // Changing from USD to CCY: keep current date (BE will handle conversion)
    return date;
  }
}

export function CalendarCurrency(props: ICalendarCurrencyProps) {
  const {
    initialValue = null,
    value,
    maxDate,
    minDate,
    onChange,
    onAccept,
    disabled,
    textFieldProps,
    shortcutItems,
    shortcutChangeImportance,
    closeOnSelect,
    sx,
    debounceTime = 1000,
    selectedCurrency = defaultCurrency,
  } = props;

  const [hasError, setHasError] = useState(false);
  const [viewDate, setViewDate] = useState(() =>
    initialValue ? convertDateForCurrencyChange(initialValue, selectedCurrency === defaultCurrency) : null
  );
  // `??` would read a caller's explicit null as "uncontrolled", leaving the field impossible to clear.
  const date = value !== undefined ? value : viewDate;

  // Determine views based on selected currency
  const views = useMemo((): readonly DateView[] => {
    return selectedCurrency === defaultCurrency
      ? (['year', 'month', 'day'] as const)
      : (['year', 'month'] as const);
  }, [selectedCurrency]);

  // Effect to handle currency changes
  useEffect(() => {
    if (date && isValid(date)) {
      const convertedDate = convertDateForCurrencyChange(date, selectedCurrency === defaultCurrency);
      if (convertedDate.getTime() !== date.getTime()) {
        setViewDate(convertedDate);
        onChange(convertedDate);
        // A currency switch is a decided change, not a draft, and happens with the popup closed.
        onAccept?.(convertedDate);
      }
    }
  }, [selectedCurrency, date, onChange, onAccept]);

  // Typing lands a keystroke at a time, so hold "Invalid date" back until the field settles.
  const flagInvalid = useMemo(() => debounce(() => setHasError(true), debounceTime), [debounceTime]);
  useEffect(() => () => flagInvalid.cancel(), [flagInvalid]);

  // A caller replacing the shown date (a step, or dropping a bad edit) settles the error with it.
  useEffect(() => {
    if (date === null || isDateInputValid(date, minDate, maxDate)) {
      flagInvalid.cancel();
      setHasError(false);
    }
  }, [date, minDate, maxDate, flagInvalid]);

  const handleChange = (newValue: Date | null) => {
    flagInvalid.cancel();
    if (newValue !== null && !isDateInputValid(newValue, minDate, maxDate)) {
      flagInvalid();
    } else {
      setHasError(false);
      setViewDate(newValue);
    }
    // Forwarded even when invalid: a date typed a digit at a time passes through real days on the way,
    // and a caller that never saw the final keystroke would apply one of those.
    onChange(newValue);
  };

  const handleAccept = (newValue: Date | null) => {
    flagInvalid.cancel();
    if (newValue !== null && !isDateInputValid(newValue, minDate, maxDate)) {
      setHasError(true);
      return;
    }
    setHasError(false);
    onAccept?.(newValue);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div css={fieldWithErrorCss}>
        <DatePicker
          slotProps={{
            shortcuts: {
              items: shortcutItems ?? defaultShortcuts,
              changeImportance: shortcutChangeImportance,
              sx: shortcutsSx,
            },
            textField: {
              ...textFieldProps,
            },
          }}
          maxDate={maxDate}
          minDate={minDate}
          views={views}
          onAccept={handleAccept}
          value={date}
          onChange={handleChange}
          onOpen={props.onOpen}
          onClose={props.onClose}
          closeOnSelect={closeOnSelect}
          disabled={disabled}
          sx={sx}
        />
        <div css={errorBelowFieldCss}>
          <ErrorText sx={{ height: 'unset' }}>{hasError ? 'Invalid date' : ''}</ErrorText>
        </div>
      </div>
    </LocalizationProvider>
  );
}
